import {useState} from 'react'
import {useAnnounceArrival} from '@/features/fromAirportArrival/useAnnounceArrival'
import {hasArrivalGuide} from '@/features/fromAirportArrival/arrivalEligibility'

/**
 * "I'M AT THE PICKUP POINT" — SAID AGAIN, because the first time is often lost.
 *
 * ── ⚠️ THIS IS NOT A DUPLICATE OF THE BUTTON ON THE WALK SCREENS ──────────────────
 * It is the fix for a race those screens cannot win. Arrival is not an endpoint: the
 * passenger's app posts the booth's coordinate to the ordinary location stream and the
 * driver's app works out the rest by measuring it. Two things then go wrong on their own:
 *
 *  · A ping sent BEFORE the driver has reached the terminal arrives at a screen that is
 *    not yet asking the question. Their app latches "passenger arrived" only once they
 *    have tapped their own Arrived; before that the fix is just a pin that moves.
 *  · Live location sharing overwrites it within about a second. The booth coordinate is
 *    replaced by the passenger's real one, which — if they have wandered ten metres to a
 *    bench — can fall outside the driver's fifty-metre test.
 *
 * So the signal is genuinely fire-and-forget and genuinely droppable, and the only honest
 * remedy is a way to send it again. The mobile app reached the same conclusion: this is
 * its `PickupActionsCard`.
 *
 * ── ⚠️ AND IT SAYS WHAT IT DID, unlike the walk screens ───────────────────────────
 * There, pressing it navigates — the screen change IS the feedback. Here nothing moves, so
 * without a line of text the button looks broken and gets pressed four more times. The
 * endpoint reports nothing back, so the line says what was SENT rather than claiming the
 * driver received it.
 */
export default function ArrivalResendCard({trip}) {
    const {announce} = useAnnounceArrival(trip)
    const [sentAt, setSentAt] = useState(null)

    /* ⚠️ FROM-AIRPORT RIDES WITH A SURVEYED TERMINAL ONLY. Everywhere else there is no
       booth to name, and "I've arrived" on a kerbside pickup is a sentence the driver's app
       has no way to act on. The same gate the whole arrival flow hangs off. */
    if (!hasArrivalGuide(trip)) return null

    const onPress = () => {
        /* `announce` returns false when the 3s guard is still up, so a double press does
           not reset the message and make it look like a fresh send. */
        if (announce()) setSentAt(Date.now())
    }

    return (
        <div className="white-global-section flex flex-col gap-3 max-md:gap-2.5 max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
            <div className="flex items-center gap-4 max-md:gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-(--accent-green-light) text-(--text-primary) max-md:h-9 max-md:w-9">
                    <svg className="h-5 w-5 max-md:h-4 max-md:w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="9"/>
                        <path d="m8.5 12.2 2.4 2.4 4.6-4.8"/>
                    </svg>
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block font-bold text-(--text-primary) max-md:text-sm">Already at the pickup point?</span>
                    <span className="block text-(--text-secondary) max-md:text-xs">
                        {sentAt
                            ? 'Location sent — tap again if your driver hasn’t set off.'
                            : 'Let your driver know exactly where you are waiting.'}
                    </span>
                </span>
            </div>

            <button
                type="button"
                onClick={onPress}
                className="min-h-13 w-full cursor-pointer rounded-full border-0 bg-(--bg-grey) px-6 font-bold text-(--text-primary) max-md:min-h-10 max-md:px-4 max-md:text-sm"
            >
                {sentAt ? 'Send again' : 'I’ve arrived'}
            </button>
        </div>
    )
}

export {ArrivalResendCard}
